import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export type SaveStatusValue = {
  state: SaveState;
  lastSavedAt: number | null;
  errorMessage: string | null;
  /** Register a save operation. Returns a controller to set state/error. */
  reportSaving: () => void;
  reportSaved: () => void;
  reportError: (msg: string) => void;
  reset: () => void;
};

const SaveStatusContext = createContext<SaveStatusValue | null>(null);

type ProviderProps = {
  children: ReactNode;
};

/**
 * Globaler Save-Status-Provider — sammelt alle Save-Operationen der App und
 * spielt den Status in der Topbar ab (siehe SaveIndicator).
 *
 * Mehrere parallele Saves: zeigt die "aggressivste" Statusstufe an
 * (saving > error > saved > idle). Ein erfolgreicher Save wechselt nach 3 s
 * automatisch zurück zu 'idle' — der User soll merken wenn etwas frisch
 * gespeichert wurde, aber der Indicator soll nicht ewig stehen.
 */
export function SaveStatusProvider({ children }: ProviderProps) {
  const [state, setState] = useState<SaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Counter aller laufenden Save-Operationen → wenn > 0, ist state='saving'
  const [, setActiveCount] = useState(0);

  const reportSaving = useCallback(() => {
    setActiveCount((c) => c + 1);
    setState('saving');
    setErrorMessage(null);
  }, []);

  const reportSaved = useCallback(() => {
    setActiveCount((c) => {
      const next = Math.max(0, c - 1);
      if (next === 0) {
        setState('saved');
        setLastSavedAt(Date.now());
        // Nach 3 s zurück zu idle
        setTimeout(() => {
          setState((cur) => (cur === 'saved' ? 'idle' : cur));
        }, 3000);
      }
      return next;
    });
  }, []);

  const reportError = useCallback((msg: string) => {
    setActiveCount((c) => Math.max(0, c - 1));
    setState('error');
    setErrorMessage(msg);
  }, []);

  const reset = useCallback(() => {
    setState('idle');
    setErrorMessage(null);
  }, []);

  const value = useMemo<SaveStatusValue>(
    () => ({
      state,
      lastSavedAt,
      errorMessage,
      reportSaving,
      reportSaved,
      reportError,
      reset,
    }),
    [state, lastSavedAt, errorMessage, reportSaving, reportSaved, reportError, reset],
  );

  return (
    <SaveStatusContext.Provider value={value}>
      {children}
    </SaveStatusContext.Provider>
  );
}

/**
 * Hook für Components die den globalen Save-Status lesen wollen
 * (z.B. SaveIndicator in der Topbar).
 *
 * Wirft NICHT wenn ausserhalb Provider — gibt 'idle' zurück (graceful fallback
 * für Components die optional in Provider-Trees rendern).
 */
export function useSaveStatus(): SaveStatusValue {
  const ctx = useContext(SaveStatusContext);
  if (!ctx) {
    // Fallback — z.B. Public-Pages haben keinen Provider
    return {
      state: 'idle',
      lastSavedAt: null,
      errorMessage: null,
      reportSaving: () => {},
      reportSaved: () => {},
      reportError: () => {},
      reset: () => {},
    };
  }
  return ctx;
}
